/**
 * CR-8276: quantities that reach the screen as text.
 *
 * The line form formats its quantities with the tenant's `formatQuantity`
 * (CR-7262: up to three decimals, trailing zeros trimmed). Audit rows and
 * change-request cards instead carry the database's numeric(12,3) as text
 * ("120.000"), or quantities baked into server-built sentences
 * ("GRN00462: 11.000 of 120.000 received"), so the two panels of the PO
 * line modal disagreed. These helpers rewrite that text on display — as the
 * Changelog already does for ISO dates — so history already written reads
 * correctly too. Nothing stored changes.
 */

export type QuantityFormatter = (value: number | string | null | undefined) => string;

/** A field whose values are quantities, by name. */
export const QUANTITY_FIELD = /qty|quantity/i;

const PLAIN_NUMBER = /^-?\d+(?:\.\d+)?$/;

/**
 * Any number in free text, except digits glued to a letter, hyphen, colon or
 * slash (a GRN reference, a time, a date), the tail of another decimal, and a
 * percentage, which keeps its own precision. A sentence's full stop after the
 * number ("45.000.") is not part of it.
 */
const QUANTITY_TOKEN = /(?<![\w:/-])(?<!\d\.)\d+(?:\.\d+)?(?![\w%:/-])(?!\.\d)/g;

/**
 * A number wearing the database's three decimals. A price carries two and a
 * date or time none, so the shape alone picks stored quantities out of a
 * reason such as "Quantity change accepted: 39.000 → 45.000.".
 */
const STORED_QUANTITY = /(?<![\w:/-])(?<!\d\.)\d+\.\d{3}(?![\w%:/-])(?!\.\d)/g;

/** A field value: formatted when the field is a quantity and the value a plain number. */
export function formatQuantityValue(field: string, value: string, formatQuantity: QuantityFormatter): string {
  return QUANTITY_FIELD.test(field) && PLAIN_NUMBER.test(value) ? formatQuantity(value) : value;
}

/** Server-built receipt text: every quantity in it, regrouped for display. */
export function formatQuantityTokens(text: string, formatQuantity: QuantityFormatter): string {
  return text.replace(QUANTITY_TOKEN, (m) => formatQuantity(m));
}

/** Free text about a quantity: its stored three-decimal numbers only. Other text is returned as is. */
export function formatStoredQuantities(text: string, formatQuantity: QuantityFormatter): string {
  return QUANTITY_FIELD.test(text) ? text.replace(STORED_QUANTITY, (m) => formatQuantity(m)) : text;
}

/**
 * A stored sentence that names a quantity ("Line 1 · P-SBC-7748 · qty 120.000
 * · @ 74.680 · due 2026-01-08", the PO-line-created audit row): only the
 * number right after "qty" / "quantity" is a quantity, so the price beside it
 * keeps its own decimals.
 */
const QUANTITY_MENTION = /\b(qty|quantity)(\s+)(\d+(?:\.\d+)?)(?![\w%:/-])(?!\.\d)/gi;

export function formatQuantityMentions(text: string, formatQuantity: QuantityFormatter): string {
  return text.replace(QUANTITY_MENTION, (_m, word: string, gap: string, num: string) => `${word}${gap}${formatQuantity(num)}`);
}
